import { useEffect } from 'react';

// WebKit renders the text caret on a separate compositing layer outside the
// DOM, so it ignores z-index and overflow clipping. When the user scrolls and
// a sticky header slides over a focused input, the caret remains visible on
// top of the header. Blurring the active editable on touchmove forces WebKit
// to tear down that caret layer.
//
// We listen to touchmove (not scroll) so the iOS auto-scroll that fires when
// the on-screen keyboard opens does not blur the field the user just focused.

const NON_EDITABLE_INPUT_TYPES = new Set([
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'file',
  'image',
  'hidden',
  'range',
  'color',
]);

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac; distinguish by touch support.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function isEditable(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type.toLowerCase();
    return !NON_EDITABLE_INPUT_TYPES.has(type);
  }
  return (el as HTMLElement).isContentEditable;
}

export function useIosCaretFix(): void {
  useEffect(() => {
    if (!isIos()) return;

    const handleTouchMove = (e: TouchEvent) => {
      const active = document.activeElement;
      if (!isEditable(active)) return;
      const target = e.target;
      if (target instanceof Node && active.contains(target)) return;
      (active as HTMLElement).blur();
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: true, capture: true });
    return () => {
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
    };
  }, []);
}
